import React from 'react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Container,
  Flex,
  Heading,
  Section,
  Text,
} from '@/components/ui';
import { StatusChip } from '@/components/ui/StatusChip';
import {
  defaultStatusMapping,
  type EntityType,
  entityTypeOverrides,
} from '@/components/ui/StatusChip/config';
import { Tooltip } from '@radix-ui/themes';

// Define all statuses for each entity type
const entityStatuses: Record<EntityType, string[]> = {
  import_mapping: [
    'created',
    'valid_for_import',
    'invalid_for_import',
    'imported',
    'failed',
  ],
  file: [
    'ready-to-check',
    'duplicated',
    'allowed',
    'not-allowed',
    'exists',
    'pending',
    'uploading',
    'uploaded',
    'failed',
  ],
  upload: ['pending', 'uploading', 'completed', 'error', 'cancelled'],
  wizard_step: ['pending', 'current', 'processing', 'completed', 'error'],
  file_mapping: [
    'excellent',
    'good',
    'partial',
    'poor',
    'missing-required',
    'missing-required-metadata',
    'unmapped',
  ],
  import: ['pending', 'processing', 'completed', 'failed'],
  csv_file: ['valid', 'invalid'],
};

// Get all unique statuses from default mapping
const allDefaultStatuses = Object.keys(defaultStatusMapping);

// Group statuses by entity type for demo
const demoData = Object.entries(entityStatuses).map(
  ([entityType, statuses]) => ({
    entityType: entityType as EntityType,
    statuses,
    title: entityType
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' '),
  })
);

// Add a group for default statuses (not specific to any entity)
const defaultStatusesGroup = {
  entityType: undefined as EntityType | undefined,
  statuses: allDefaultStatuses.filter(
    status => !Object.values(entityStatuses).flat().includes(status)
  ),
  title: 'Default Statuses (Common)',
};

export const StatusChipDemoPage: React.FC = () => {
  return (
    <Container pt="4" pb="4" size="expand">
      <Section>
        <Flex direction="column" gap="6">
          <div>
            <Heading size="large" className="mb-2">
              StatusChip Component Demo
            </Heading>
            <Text size="2" color="gray">
              Universal status badge component with automatic mapping based on
              entity type and status. Supports three levels of configuration
              priority: explicit props, entity-specific overrides, and default
              mapping.
            </Text>
          </div>

          {/* Default Status Mappings - All */}
          <Card>
            <CardHeader>
              <CardTitle>Default Status Mappings</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <Text size="2" color="gray">
                  All default status mappings that apply to all entity types
                  unless overridden. These are the base configurations used when
                  no entity-specific override is defined.
                </Text>
                <Flex gap="4" wrap="wrap">
                  {allDefaultStatuses.map(status => (
                    <Tooltip key={`default-${status}`} content={status}>
                      <StatusChip status={status} size="large" />
                    </Tooltip>
                  ))}
                </Flex>
              </Flex>
            </CardContent>
          </Card>

          {/* Default Statuses (Not in any entity type) */}
          {defaultStatusesGroup.statuses.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle>{defaultStatusesGroup.title}</CardTitle>
              </CardHeader>
              <CardContent>
                <Flex direction="column" gap="4">
                  <Text size="2" color="gray">
                    These statuses are defined in default mapping but not used
                    by any specific entity type.
                  </Text>
                  <Flex gap="4">
                    {defaultStatusesGroup.statuses.map(status => (
                      <StatusChip key={status} status={status} />
                    ))}
                  </Flex>
                </Flex>
              </CardContent>
            </Card>
          )}

          {/* Entity Type Groups */}
          {demoData.map(({ entityType, statuses, title }) => (
            <Card key={entityType}>
              <CardHeader>
                <CardTitle>{title}</CardTitle>
              </CardHeader>
              <CardContent>
                <Flex direction="column" gap="4">
                  <Text size="2" color="gray">
                    Entity type: <code>{entityType}</code>
                    {entityTypeOverrides[entityType] && (
                      <span className="ml-2 text-info">
                        (has entity-specific overrides)
                      </span>
                    )}
                  </Text>
                  <Flex gap="4">
                    {statuses.map(status => (
                      <Tooltip
                        key={`${entityType}-${status}`}
                        content={
                          <>
                            entityType: {entityType} <br />
                            status: {status}
                          </>
                        }
                      >
                        <StatusChip
                          size="large"
                          // key={`${entityType}-${status}`}
                          entityType={entityType}
                          status={status}
                        />
                      </Tooltip>
                    ))}
                  </Flex>
                </Flex>
              </CardContent>
            </Card>
          ))}

          {/* Override Examples */}
          <Card>
            <CardHeader>
              <CardTitle>Override Examples</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <Text size="2" color="gray">
                  Examples of overriding mapped values with explicit props.
                </Text>
                <div className="flex flex-wrap gap-3">
                  <StatusChip
                    entityType="file"
                    status="uploading"
                    color="primary"
                    label="Custom Color"
                  />
                  <StatusChip
                    entityType="wizard_step"
                    status="current"
                    variant="outlined"
                    label="Custom Variant"
                  />
                  <StatusChip
                    entityType="import_mapping"
                    status="created"
                    size="large"
                    label="Custom Size"
                  />
                  <StatusChip
                    status="pending"
                    color="success"
                    variant="solid"
                    label="Fully Overridden"
                  />
                </div>
              </Flex>
            </CardContent>
          </Card>

          {/* Usage Examples */}
          <Card>
            <CardHeader>
              <CardTitle>Usage Examples</CardTitle>
            </CardHeader>
            <CardContent>
              <Flex direction="column" gap="4">
                <div>
                  <Text size="2" weight="medium" className="mb-2">
                    Basic usage with default mapping:
                  </Text>
                  <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                    {`<StatusChip status="pending" />`}
                  </pre>
                </div>
                <div>
                  <Text size="2" weight="medium" className="mb-2">
                    With entity type (uses entity-specific override if
                    available):
                  </Text>
                  <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                    {`<StatusChip entityType="import_mapping" status="created" />`}
                  </pre>
                </div>
                <div>
                  <Text size="2" weight="medium" className="mb-2">
                    Override specific props:
                  </Text>
                  <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                    {`<StatusChip 
  entityType="file" 
  status="uploading" 
  color="primary" 
/>`}
                  </pre>
                </div>
                <div>
                  <Text size="2" weight="medium" className="mb-2">
                    In DataGrid (StatusCell):
                  </Text>
                  <pre className="bg-gray-100 p-3 rounded text-sm overflow-x-auto">
                    {`<DataCell 
  type="status" 
  entityType="import_mapping" 
  value="created" 
/>`}
                  </pre>
                </div>
              </Flex>
            </CardContent>
          </Card>
        </Flex>
      </Section>
    </Container>
  );
};
